import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";

import { setNewPassword } from "../auth/cognito";
import signInBackground from "../assets/signin-iot-bg.png";

// Passed from SignInPage when Cognito returns new_password_required.
interface SetPasswordState {
  session: string;
  email: string;
}

export function SetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SetPasswordState | null;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Reached directly (no challenge session) → back to sign-in.
  if (!state?.session || !state?.email) {
    return <Navigate to="/sign-in" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!state) return;
    setError(null);
    if (password !== confirm) {
      setError("The passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const resp = await setNewPassword({ email: state.email, session: state.session, newPassword: password });
      if (resp.state === "authenticated") {
        navigate("/", { replace: true });
        return;
      }
      if (resp.state === "mfa_enrollment_required") {
        navigate("/mfa-enrollment", { replace: true, state: { accessToken: resp.accessToken, email: state.email } });
        return;
      }
      // An admin may still need to set up / verify MFA after choosing a password.
      navigate("/mfa-challenge", {
        replace: true,
        state: {
          state: resp.state,
          session: resp.session,
          email: state.email,
          secretCode: resp.state === "mfa_setup" ? resp.secretCode : undefined,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set your password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-riot-navy-950">
      <div className="absolute inset-0">
        <img src={signInBackground} alt="" aria-hidden="true" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-riot-navy-950/58" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_22%,rgba(6,10,22,0.55)_52%,rgba(0,0,0,0.94)_100%)]" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-10 sm:px-8 lg:justify-end lg:pr-16 xl:pr-24">
        <div className="w-full max-w-sm">
          <div className="rounded-[28px] border border-white/12 bg-riot-navy-950/68 px-8 py-10 shadow-[0_28px_90px_rgba(2,8,23,0.55)] backdrop-blur-md">
            <div className="mb-8 flex flex-col items-center text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-riot-gold-400/12 ring-1 ring-riot-gold-400/25">
                <KeyRound size={24} className="text-riot-gold-300" />
              </div>
              <h1 className="text-2xl font-bold text-white">Set your password</h1>
              <p className="mt-2 max-w-xs text-sm text-riot-navy-200/80">
                Welcome to RIoT-FOTA. Choose a permanent password to finish setting up{" "}
                <span className="font-medium text-riot-navy-100">{state.email}</span>.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="new-password" className="mb-1 block text-xs font-medium text-riot-navy-100/90">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="At least 12 characters"
                  className="w-full rounded-xl border border-white/10 bg-white/6 px-3 py-2.5 text-sm text-white shadow-sm placeholder:text-riot-navy-300/70 focus:border-riot-gold-400/70 focus:outline-none focus:ring-2 focus:ring-riot-gold-400/30"
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="mb-1 block text-xs font-medium text-riot-navy-100/90">
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  className="w-full rounded-xl border border-white/10 bg-white/6 px-3 py-2.5 text-sm text-white shadow-sm placeholder:text-riot-navy-300/70 focus:border-riot-gold-400/70 focus:outline-none focus:ring-2 focus:ring-riot-gold-400/30"
                />
              </div>

              <p className="text-[11px] text-riot-navy-200/70">
                Use at least 12 characters with upper &amp; lower case, a number, and a symbol.
              </p>

              {error && (
                <div role="alert" className="rounded-xl border border-red-500/25 bg-red-950/35 px-3 py-2 text-xs text-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-riot-gold-400 px-4 py-2.5 text-sm font-semibold text-riot-navy-950 shadow-[0_12px_30px_rgba(212,175,55,0.22)] transition hover:bg-riot-gold-300 active:bg-riot-gold-500 focus:outline-none focus:ring-2 focus:ring-riot-gold-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Saving..." : "Set password & continue"}
              </button>
            </form>
          </div>

          <p className="mt-4 text-center text-[11px] text-riot-navy-200/55">
            RIoT-FOTA Platform &middot; Riot IO (PTY) Ltd
          </p>
        </div>
      </div>
    </div>
  );
}
